/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { useContext, useEffect, useState } from 'react'
import { Tab } from '@headlessui/react'
import CustomHeadlessTabs from 'components/CustomHeadlessTabs'
import Skeleton from 'react-loading-skeleton'

// Contexts
import { MovementsContext } from 'contexts/movements'

// Hooks / utils
import { trackEvent } from 'analytics/trackFunctions'
import { useSelectedTabStore } from 'store'
import { LIMIT_ERRORS } from 'types/api/transfer'

// Custom components
import { HelpIcon } from 'assets/icons'
import AccountInformation from 'components/AccountInformation'
import PageWrapper from 'components/PageWrapper'
import FallbackError from 'components/FallbackError'

// API
import { getBalance } from 'api/bank'
import { getCashoutLimit } from 'api/cashout'

// Page components
import TransferTab from './components/TransfersTab'
import ReceiptsTab from './components/ReceiptsTab'
import AvailableBalanceModal from './components/AvailableBalanceModal'

const { ACCOUNT_WITHOUT_BALANCE } = LIMIT_ERRORS

const Transfers = () => {
  const [cashoutLimit, setCashoutLimit] = useState<number | string>(null)
  const [cashoutLimitOrBalanceError, setCashoutLimitOrBalanceError] =
    useState<boolean>(false)

  const { selectedTab, updateSelectedTab } = useSelectedTabStore()
  const { setAmountBalance } = useContext(MovementsContext)

  useEffect(() => {
    trackEvent({ eventName: 'bille_pantalla_transferir' })
    setCashoutLimitOrBalanceError(false)
    Promise.all([getBalance(), getCashoutLimit()]).then((res) => {
      const [balanceRes, cashoutLimitRes] = res

      if (balanceRes?.balance) {
        setAmountBalance(balanceRes.balance)
      } else {
        setCashoutLimitOrBalanceError(true)
      }

      if (cashoutLimitRes.success) {
        const { cashOutLimit } = cashoutLimitRes.data.result
        setCashoutLimit(cashOutLimit)
      } else {
        const { code } = cashoutLimitRes.error

        if (code === ACCOUNT_WITHOUT_BALANCE) {
          return setCashoutLimit(0)
        }

        setCashoutLimitOrBalanceError(true)
      }
    })
  }, [])

  return (
    <PageWrapper
      viewSelected='/movimientos'
      className='!px-0 md:px-4'
      title='Transferencias'
      noTabBar
    >
      <section className='px-4 min-h-[80vh] text-black md:flex md:max-w-564 md:flex-col mb-20 md:mb-5 mx-auto'>
        {cashoutLimitOrBalanceError && (
          <FallbackError type='tu saldo disponible' />
        )}
        {!cashoutLimitOrBalanceError && typeof cashoutLimit === 'number' && (
          <AccountInformation
            isCashout
            className='mb-0 md:mb-0'
            limit={cashoutLimit}
            label={<BalanceLabel />}
          />
        )}
        {!cashoutLimitOrBalanceError && cashoutLimit === null && (
          <Skeleton height={135} />
        )}
        {/* Tabs */}
        <Tab.Group
          defaultIndex={selectedTab}
          onChange={(index) => updateSelectedTab(index)}
        >
          <CustomHeadlessTabs
            className='mt-5'
            tabs={['Transferencias', 'Comprobantes']}
          />
          <Tab.Panels className='!min-h-[55vh] transition-all duration-200'>
            <Tab.Panel className='p-1 min-h-[50vh] overflow-y-auto'>
              <TransferTab />
            </Tab.Panel>
            <Tab.Panel className='p-1 overflow-y-auto'>
              <ReceiptsTab />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </section>
    </PageWrapper>
  )
}

export default Transfers

const BalanceLabel = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false)

  return (
    <>
      <div className='w-full flex relative items-center'>
        <p className='text-center w-full'>Saldo disponible para retirar</p>
        <button
          className='absolute right-0'
          type='button'
          onClick={() => {
            trackEvent({ eventName: 'bille_pantalla_transferir_modal_monto' })
            setIsVisible(true)
          }}
        >
          <HelpIcon variant='GREEN' className='h-4 w-4' />
          <span className='sr-only'>
            ¿Qué significa saldo disponible para retirar?
          </span>
        </button>
      </div>
      <AvailableBalanceModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
      />
    </>
  )
}
